import React, { useState, useEffect } from 'react';

// Hooks
import { useForm, SubmitHandler } from "react-hook-form"

// Components

import Button from '../../components/Button/Button';
import InputField from '../../components/InputField/InputField';

// Models
import { regiones } from '../../models/models';

// Services

import { postEnviarContacto } from '../../config/services/contacto/postEnviarContacto';

// Assets

import IconoMundoGreenGascoContacto from '../../assets/images/icono-mundo-green-gasco-contacto.png';

// Styles
import './contact.css';
import { Comuna, Region, Solucion } from '../../interfaces/types';

import { getSolucionesEnergeticas } from '../../config/services/strapi/strapi';

import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

type Inputs = {
  nombre: string;
  correo: string;
  telefono: string;
  nombreEmpresa: string;
  region: string;
  comuna: string;
  tipoEmpresa: string;
  cliente: string;
  tipoSolucion: string;
  comentario: string;
}

function Contact() {
  const [comunas, setComunas] = useState<Comuna[]>([]);
  const [soluciones, setSoluciones] = useState<Partial<Solucion>[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue
  } = useForm<Inputs>();

  const handleChangeRegion = (event: React.ChangeEvent<HTMLSelectElement>) => {
    clearErrors("region");

    const selectedRegion = event.target.value;
    const selectedComunas = regiones.find(region => region.nombre === selectedRegion)?.comunas || [];
    setComunas(selectedComunas);

    setValue("comuna", "");
  };

  const onSubmit: SubmitHandler<Inputs> = async(data) => {
    try {
      const payload = {
        ...data,
        cliente: data.cliente === "true" ? true : false
      }

      const accessToken = "";

      await postEnviarContacto(accessToken, payload);

      setValue("nombre", "");
      setValue("correo", "");
      setValue("telefono", "");
      setValue("nombreEmpresa", "");
      setValue("region", "");
      setValue("comuna", "");
      setValue("tipoEmpresa", "");
      setValue("tipoSolucion", "");
      setValue("cliente", "false");
      setValue("comentario", "");

      toast.success("Formulario enviado correctamente. Nos pondremos en contacto contigo pronto.");
    }
    catch(error: AxiosError | any) {
      if (error.response && error.response.status === 500) {
        toast.error("Error al enviar el formulario. Por favor, inténtalo de nuevo más tarde.");
      }

      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        console.error("Error de validación:", errorData);
        const errorMessage = errorData.data.message || "Error al enviar el formulario. Por favor, revisa los datos ingresados.";

        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg) => toast.error(msg));
        } else {
          toast.error(errorMessage);
        }
      }
      console.warn(error);
    }
  }

  useEffect(() => {
    const fetchSoluciones = async () => {
      const rawSoluciones = await getSolucionesEnergeticas({
        visible: true
      });
      
      const mappedSoluciones: Partial<Solucion>[] = rawSoluciones.map((solucion: any) => ({
        id: solucion.id,
        beneficios: solucion.beneficios,
        caracteristicas: solucion.caracteristicas,
        descripcion: solucion.descripcion,
        externo: solucion.externo,
        icono: solucion.icono,
        imagenBeneficios: solucion.imagen_beneficios,
        imagenIndustria: solucion.imagen_industria,
        imagenProyectos: solucion.imagen_proyectos,
        industrias: solucion.industrias,
        nombre: solucion.nombre,
        portada: {
          url: solucion.portada.url
        },
        url: solucion.url,
      }));

      setSoluciones(mappedSoluciones);
    };

    fetchSoluciones();

    setValue("cliente", "false");
  }, []);

  return (
    <main className=''>
      <section className='flex flex-col flex-col-reverse lg:flex-row items-start justify-between py-[34px] px-4 md:px-[102px]'>
        <div className="w-full flex flex-col items-center pr-0 lg:pr-[17px]">
          <figure className='flex justify-center items-center !m-0 !p-10 lg:!px-0 lg:!pt-0 lg:!pb-[32px] lg:w-[246.5px] lg:h-auto'>
            <img src={IconoMundoGreenGascoContacto} className='w-full h-full' alt="Icono Mundo Gasco Contacto" />
          </figure>
          <p className='text-[25.5px] font-[600] !leading-[37.7px] text-[var(--text-color-primary)]'>
            Miles de empresas en Chile ya confían en Gasco.
          </p>
        </div>
        <div className='w-full pb-[71px] lg:pb-[0px]'>
          <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
            <h3 className='!text-[34px] !leading-[37.4px] !text-[var(--text-color-primary)] !mb-[34px] !p-0 !mx-0 !mt-0'>¿Te ayudamos a hacer más eficiente y sustentable tu negocio?</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className='col-span-2'>
                <InputField
                  placeholder='NOMBRE COMPLETO'
                  type='text'
                  {...register("nombre", { required: true })}
                  onInput={() => clearErrors("nombre")}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.nombre && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2'>
                <InputField
                  placeholder='EMAIL'
                  type='email'
                  {...register("correo", { required: true })}
                  onInput={() => clearErrors("correo")}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.correo && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2'>
                <InputField
                  placeholder='TELÉFONO'
                  type='tel'
                  {...register("telefono", { required: true })}
                  onInput={() => clearErrors("telefono")}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.telefono && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2 mb-1'>
                <InputField
                  placeholder='NOMBRE DE LA EMPRESA'
                  type='text'
                  {...register("nombreEmpresa", { required: true })}
                  onInput={() => clearErrors("nombreEmpresa")}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.nombreEmpresa && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2 md:col-span-1 mb-4'>
                <div className="relative inline-block w-full">
                  <select
                    className="!text-[11.9px] block appearance-none w-full bg-white border border-[rgb(153,153,153)] hover:border-[rgb(34,36,40)] text-[var(--text-color-primary)] py-2 px-4 pr-8 rounded shadow leading-tight focus:outline-none focus:ring-2 focus:ring-[--text-color-secondary] focus:border-[var(--text-color-secondary)]"
                    {...register("region", { required: true })}
                    onChange={handleChangeRegion}
                  >
                    <option disabled selected hidden value="">Región</option>
                    {
                      regiones.map((region: Region) => (
                        <option
                          key={region.id}
                          value={region.nombre}
                        >
                          {region.nombre}
                        </option>
                      ))
                    }
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                      <path d="M5.516 7.548l4.484 4.486 4.484-4.486L15.93 8.93l-5.93 5.93-5.93-5.93z"/>
                    </svg>
                  </div>
                </div>
                <p className='text-red-600 text-lg font-medium'>
                  {errors.region && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2 md:col-span-1 mb-4'>
                <div className="relative inline-block w-full">
                  <select
                    className="!text-[11.9px] block appearance-none w-full bg-white border border-[rgb(153,153,153)] hover:border-[rgb(34,36,40)] text-[var(--text-color-primary)] py-2 px-4 pr-8 rounded shadow leading-tight focus:outline-none focus:ring-2 focus:ring-[--text-color-secondary] focus:border-[var(--text-color-secondary)]"
                    {...register("comuna", { required: true })}
                    onChange={() => clearErrors("comuna")}
                  >
                    <option value="">Comuna</option>
                    {
                      comunas.map((comuna: Comuna) => (
                        <option
                          key={comuna.id}
                          value={comuna.nombre}
                        >
                          {comuna.nombre}
                        </option>
                      ))
                    }
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                      <path d="M5.516 7.548l4.484 4.486 4.484-4.486L15.93 8.93l-5.93 5.93-5.93-5.93z"/>
                    </svg>
                  </div>
                </div>
                <p className='text-red-600 text-lg font-medium'>
                  {errors.comuna && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2'>
                <InputField
                  placeholder='RUBRO O INDUSTRIA'
                  type='text'
                  {...register("tipoEmpresa", { required: true })}
                  onInput={() => clearErrors("tipoEmpresa")}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.tipoEmpresa && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2 w-full flex justify-between items-center mb-2'>
                <p className='w-full uppercase'>¿Es Hoy Cliente Gasco?</p>
                <div className='w-full flex justify-start items-center gap-6'>
                  <label className="flex items-center gap-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="true"
                      {...register("cliente", { required: true })}
                      className="appearance-none w-5 h-5 border-2 border-[var(--text-color-primary)] rounded-full checked:bg-white checked:border-[var(--text-color-secondary)] focus:outline-none relative
                        before:content-[''] before:block before:w-3 before:h-3 before:rounded-full before:bg-[var(--text-color-secondary)] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 checked:before:opacity-100 before:opacity-0 transition-all"
                    />
                    <span className="text-black">Sí</span>
                  </label>
                  <label className="flex items-center gap-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="false"
                      {...register("cliente", { required: true })}
                      className="appearance-none w-5 h-5 border-2 border-[var(--text-color-primary)] rounded-full checked:bg-white checked:border-[var(--text-color-secondary)] focus:outline-none relative
                        before:content-[''] before:block before:w-3 before:h-3 before:rounded-full before:bg-[var(--text-color-secondary)] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 checked:before:opacity-100 before:opacity-0 transition-all"
                    />
                    <span className="text-black">No</span>
                  </label>
                </div>
              </div>
              <div className='col-span-2 mb-4'>
                <div className="relative inline-block w-full">
                  <select
                    className="!text-[11.9px] block appearance-none w-full bg-white border border-[rgb(153,153,153)] hover:border-[rgb(34,36,40)] text-[var(--text-color-primary)] py-2 px-4 pr-8 rounded shadow leading-tight focus:outline-none focus:ring-2 focus:ring-[--text-color-secondary] focus:border-[var(--text-color-secondary)]"
                    {...register("tipoSolucion", { required: true })}
                    onChange={() => clearErrors("tipoSolucion")}
                  >
                    <option value="">¿QUÉ TIPO DE SOLUCIÓN NECESITAS?</option>
                    {
                      soluciones.map((solucion: Partial<Solucion>) => (
                        <option
                          key={solucion.id}
                          value={solucion.nombre}
                        >
                          {solucion.nombre}
                        </option>
                      ))
                    }
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                      <path d="M5.516 7.548l4.484 4.486 4.484-4.486L15.93 8.93l-5.93 5.93-5.93-5.93z"/>
                    </svg>
                  </div>
                </div>
                <p className='text-red-600 text-lg font-medium'>
                  {errors.tipoSolucion && "Este campo es obligatorio"}
                </p>
              </div>
              <div className='col-span-2'>
                <textarea
                  className="!text-[11.9px] block w-full bg-white border border-[rgb(153,153,153)] hover:border-[rgb(34,36,40)] text-[var(--text-color-primary)] py-2 px-4 rounded shadow leading-tight focus:outline-none focus:ring-2 focus:ring-[--text-color-secondary] focus:border-[var(--text-color-secondary)]"
                  placeholder='ESCRIBE TU COMENTARIO'
                  {...register("comentario", { required: true })}
                  onInput={() => clearErrors("comentario")}
                  rows={5}
                />
                <p className='text-red-600 text-lg font-medium'>
                  {errors.comentario && "Este campo es obligatorio"}
                </p>
              </div>
              <div className="col-span-2">
                <Button
                  fill='solid'
                  size='large'
                  type='submit'
                  onClick={handleSubmit(onSubmit)}
                >
                  Enviar
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </main>
  )
}

export default Contact